import { createContext, useState, useEffect } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const AUTH_URL = `${API_URL}/authRoutes`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [deliveryConfig, setDeliveryConfig] = useState(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (token) {
        try {
          const res = await axios.get(`${AUTH_URL}/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          // Check if response includes roles and permissions (new format)
          // If not, it's the old format with just user data
          let userData;
          if (res.data.roles || res.data.permissions) {
            // New format with roles/permissions
            userData = { ...res.data, token };
          } else {
            // Old format - just user data
            userData = { ...res.data, token };
          }

          setUser(userData);

          // Fetch delivery config for the logged-in customer
          try {
            const dcRes = await axios.get(`${API_URL}/menuRoutes/delivery-config`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setDeliveryConfig(dcRes.data);
          } catch { /* non-fatal */ }

          setLoading(false);
        } catch (err) {
          console.error('Error fetching user profile:', err);

          // Only logout if error is 401 (Unauthorized)
          if (err.response && err.response.status === 401) {
            logout();
          }

          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (username, password) => {
    try {
      const res = await axios.post(`${AUTH_URL}/login`, { username, password });

      if (res.status === 200) {
        localStorage.setItem('token', res.data.token);
        setToken(res.data.token);

        // Fetch user profile with roles/permissions after login
        try {
          const profileRes = await axios.get(`${AUTH_URL}/profile`, {
            headers: { Authorization: `Bearer ${res.data.token}` },
          });

          // Merge login response with profile data
          const userData = {
            ...res.data.user,
            ...profileRes.data,
            token: res.data.token
          };

          if (userData.user_type === 'P') {
            localStorage.setItem('checkoutMode', 'live');
          }

          setUser(userData);
          return userData;
        } catch (profileErr) {
          console.error('Error fetching profile after login:', profileErr);
          // Fallback to just login data if profile fetch fails
          const userData = { ...res.data.user, token: res.data.token };
          if (userData.user_type === 'P') {
            localStorage.setItem('checkoutMode', 'live');
          }
          setUser(userData);
          return userData;
        }
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (err) {
      // Enhanced error logging for debugging
      console.error('Full login error:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      console.error('Request URL:', `${AUTH_URL}/login`);

      if (err.response && err.response.status === 401) {
        throw new Error('Invalid username or password');
      } else if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
        throw new Error('Cannot connect to server. Please ensure the backend is running.');
      } else if (err.response) {
        throw new Error(`Server error: ${err.response.status} - ${err.response.data?.message || 'Please try again later'}`);
      } else {
        throw new Error('Server error, please try again later');
      }
    }
  };

  const loginWithOtp = async (phone, otp, endpoint = '/otp-login/verify') => {
    try {
      const res = await axios.post(`${AUTH_URL}${endpoint}`, { phone, otp });
      if (res.status === 200) {
        localStorage.setItem('token', res.data.token);
        setToken(res.data.token);
        try {
          const profileRes = await axios.get(`${AUTH_URL}/profile`, {
            headers: { Authorization: `Bearer ${res.data.token}` },
          });
          const userData = { ...res.data.user, ...profileRes.data, token: res.data.token };
          if (userData.user_type === 'P') localStorage.setItem('checkoutMode', 'live');
          setUser(userData);
          return userData;
        } catch {
          const userData = { ...res.data.user, token: res.data.token };
          if (userData.user_type === 'P') localStorage.setItem('checkoutMode', 'live');
          setUser(userData);
          return userData;
        }
      }
    } catch (err) {
      if (err.response?.status === 400) throw new Error(err.response.data?.error || 'Invalid or expired OTP.');
      if (err.response?.status === 429) throw new Error(err.response.data?.error || 'Too many attempts. Please wait.');
      throw new Error('Sign in failed. Please try again.');
    }
  };

  const loginWithMagicLink = async (magicToken) => {
    const res = await axios.post(`${AUTH_URL}/magic-login`, { token: magicToken });
    if (res.status !== 200) throw new Error('Magic link sign-in failed.');
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    const tab = res.data.tab || null;
    try {
      const profileRes = await axios.get(`${AUTH_URL}/profile`, {
        headers: { Authorization: `Bearer ${res.data.token}` },
      });
      const userData = { ...res.data.user, ...profileRes.data, token: res.data.token };
      setUser(userData);
      return { user: userData, tab };
    } catch {
      const userData = { ...res.data.user, token: res.data.token };
      setUser(userData);
      return { user: userData, tab };
    }
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${AUTH_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser({ ...res.data, token });
    } catch { /* silent */ }
  };

  const refreshDeliveryConfig = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/menuRoutes/delivery-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeliveryConfig(res.data);
    } catch { /* non-fatal */ }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setToken(null);
    setDeliveryConfig(null);
  };

  const switchUser = (newToken) => {
    localStorage.setItem('token', newToken);
    localStorage.removeItem('checkoutMode');
    setUser(null);
    setDeliveryConfig(null);
    setLoading(true);
    setToken(newToken);
  };

  // Function to check if the user has access to a specific page
  const pageAccess = async (page_name, role) => {
    try {
      // Allow 'SADM' role to access all pages
      if (role === 'SADM') {
        return true;
      }
      const response = await axios.get(
        `${AUTH_URL}/pageAccess?page_name=${page_name}&role=${role}`
      );
      return response.data.access;
    } catch (error) {
      console.error('Error checking page access:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithOtp, loginWithMagicLink, logout, loading, pageAccess, deliveryConfig, refreshUser, refreshDeliveryConfig, switchUser }}>
      {children}
    </AuthContext.Provider>
  );
};
